import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { DataRoomFile } from "@/types/dataRoom"
import { IconClipboardList, IconDownload } from "@tabler/icons-react"

import { useReducedMotion } from "motion/react"
import { useEffect, useRef, type RefObject } from "react"
import { WELCOME_MOTION } from "./welcome/welcomeMotion"

interface DataRoomChecklistCardProps {
  buttonRef?: RefObject<HTMLButtonElement | null>
  pulse?: number
  checklist: DataRoomFile
  onOpen: (file: DataRoomFile) => void
  onDownload: (file: DataRoomFile) => void
}

/** The starting point of the room: the one document that says which
 * uploads are needed. Sits above the folders so a first-time customer
 * reads it before exploring. */
export const DataRoomChecklistCard: React.FC<DataRoomChecklistCardProps> = ({
  checklist,
  buttonRef,
  pulse = 0,
  onOpen,
  onDownload,
}) => {
  const { translate } = useTranslation()
  const surfaceRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  useEffect(() => {
    if (!pulse || reduced) return
    const animation = surfaceRef.current?.animate?.(
      [
        { boxShadow: "0 0 0 0 rgba(255,244,64,0)" },
        { boxShadow: "0 0 0 6px rgba(255,244,64,0.25)" },
        { boxShadow: "0 0 0 0 rgba(255,244,64,0)" },
      ],
      {
        duration: WELCOME_MOTION.glow * 1000,
        easing: `cubic-bezier(${WELCOME_MOTION.ease.join(",")})`,
      },
    )
    return () => animation?.cancel()
  }, [pulse, reduced])
  return (
    <div
      ref={surfaceRef}
      className="flex items-center gap-2 rounded-xl bg-grey2 pr-2"
      data-testid="data-room-checklist"
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => onOpen(checklist)}
        className="flex min-w-0 flex-1 items-center gap-4 rounded-xl px-5 py-4 text-left transition-colors hover:bg-grey4/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        <IconClipboardList
          size={24}
          className="shrink-0 text-yellow1"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <Typography variant="textM-bold">
            {translate("DATA_ROOM/CHECKLIST_OPEN")}
          </Typography>
          <Typography variant="textXS" className="mt-0.5 text-grey5">
            {translate("DATA_ROOM/CHECKLIST_HELP")}
          </Typography>
        </span>
      </button>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 text-grey5 hover:text-white"
        aria-label={translate("DATA_ROOM/DOWNLOAD_ARIA", {
          name: checklist.fileName,
        })}
        onClick={() => onDownload(checklist)}
      >
        <IconDownload size={16} aria-hidden="true" />
        {translate("DATA_ROOM/DOWNLOAD_TOOLTIP")}
      </Button>
    </div>
  )
}
